import React, { useCallback, useState, useMemo, useEffect } from "react";
import { Input, Button, message, Badge } from "antd";
import {
  PaperClipOutlined,
  CloseOutlined,
  PlusOutlined,
  SendOutlined,
} from "@ant-design/icons";
import CustomUpload from "../../../../../components/shared/CustomUpload";
import { formatFileSize } from "../../../../../utils/shared";
import { useGlobalDragDrop } from "../../../../../hooks/useGlobalDragDrop";
import { useGlobalPaste } from "../../../../../hooks/useGlobalPaste";

const { TextArea } = Input;

const FileUploadInput = ({ handleSendMessage }) => {
  const [files, setFiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [messageTexts, setMessageTexts] = useState({});

  const currentFile = useMemo(() => files[currentIndex], [files, currentIndex]);

  const handleFileSelect = useCallback((fileList) => {
    const oversizedFiles = [];
    const newFiles = fileList
      .filter((file) => file.originFileObj || file instanceof File)
      .map((file, index) => {
        const fileObj = file.originFileObj || file;
        if (fileObj.size > 3 * 1024 * 1024) {
          oversizedFiles.push(fileObj.name);
          return null;
        }
        return {
          id: `${Date.now()}-${index}`,
          file: fileObj,
          filename: fileObj.name,
          file_size_bytes: fileObj.size,
          mime_type: fileObj.type,
          preview: fileObj.type.startsWith("image/")
            ? URL.createObjectURL(fileObj)
            : null,
        };
      })
      .filter(Boolean);

    if (oversizedFiles.length > 0)
      message.error(`Files exceeding 3MB: ${oversizedFiles.join(", ")}`);

    if (newFiles.length > 0) {
      setFiles((prev) => {
        const shouldResetIndex = prev.length === 0;
        if (shouldResetIndex) {
          setCurrentIndex(0);
        }
        return [...prev, ...newFiles];
      });
    }
  }, []);

  const { isDragging } = useGlobalDragDrop({
    onFilesDrop: handleFileSelect,
  });

  useGlobalPaste(handleFileSelect);

  const removeFile = useCallback((fileId, index) => {
    setFiles((prev) => {
      const fileToRemove = prev.find((f) => f.id === fileId);

      // Cleanup object URL
      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }

      const updated = prev.filter((f) => f.id !== fileId);

      // Adjust current index if necessary
      setCurrentIndex((currentIdx) => {
        if (updated.length === 0) return 0;
        if (currentIdx >= updated.length) return updated.length - 1;
        return currentIdx;
      });

      return updated;
    });

    setMessageTexts((prev) => {
      //@ts-ignore
      const { [fileId]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const handleMessageChange = useCallback((fileId, text) => {
    setMessageTexts((prev) => ({
      ...prev,
      [fileId]: text,
    }));
  }, []);

  const handleSendAll = useCallback(() => {
    if (files.length === 0) return;

    handleSendMessage(
      files.map((f) => ({
        ...f,
        message: messageTexts[f.id],
      })),
    );

    // Cleanup all object URLs before reset
    files.forEach((file) => {
      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }
    });

    // Reset after sending all
    setFiles([]);
    setMessageTexts({});
    setCurrentIndex(0);
  }, [files, messageTexts, handleSendMessage]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        handleSendAll();
      }
    };

    window.addEventListener("keydown", handleKeyPress);

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleSendAll]);

  return (
    <div>
      {isDragging && (
        <div className="drag-overlay">
          <div className="drag-box">
            <div className="drag-icon">📂</div>
            <div className="drag-text">Drop files here</div>
          </div>
        </div>
      )}

      <div className={files.length ? "file-messenger-container" : "d-none"}>
        <div className="preview-content w-100">
          <div className="preview-header">
            <Button
              className="close-btn"
              onClick={() => {
                setFiles([]);
                setMessageTexts({});
                setCurrentIndex(0);
              }}
              icon={<CloseOutlined />}
              type="text"
              shape="circle"
            />

            <div className="file-name-display ellipsis-text">
              {currentFile?.filename}
            </div>
          </div>
          <div className="content-middle-div">
            {currentFile?.preview ? (
              <img
                src={currentFile.preview}
                alt={currentFile.filename}
                className="preview-image"
              />
            ) : (
              <div className="preview-file-icon">
                <PaperClipOutlined className="file-icon-large" />
                <div className="file-details">
                  <div className="file-name-text">{currentFile?.filename}</div>
                  <div className="file-size-text">
                    {formatFileSize(currentFile?.file_size_bytes || 0)}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="message-input-area">
            <TextArea
              className="message-textarea"
              placeholder={`Add a caption for file...`}
              value={messageTexts[currentFile?.id] || ""}
              onChange={(e) =>
                handleMessageChange(currentFile?.id, e.target.value)
              }
              autoSize={{ maxRows: 1 }}
            />
          </div>
        </div>

        <div className="preview-footer flex align-center">
          <div className="thumbnails-strip hide-scrollbar">
            {files.map((file, index) => (
              <div
                key={file.id}
                className={`thumbnail-item ${
                  currentIndex === index ? "active" : ""
                }`}
                onClick={() => setCurrentIndex(index)}
                role="button"
                tabIndex={0}
                aria-label={`View ${file.filename}`}
              >
                {file.preview ? (
                  <img
                    src={file.preview}
                    alt={file.filename}
                    className="thumbnail-image"
                  />
                ) : (
                  <div className="thumbnail-file">
                    <PaperClipOutlined />
                  </div>
                )}
                <button
                  className="thumbnail-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(file.id, index);
                  }}
                  aria-label={`Remove ${file.filename}`}
                >
                  <CloseOutlined />
                </button>
              </div>
            ))}

            <CustomUpload
              onChange={handleFileSelect}
              accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.txt,.zip,.csv"
            >
              <div className="add-more-thumbnail" role="button" tabIndex={0}>
                <PlusOutlined className="add-icon" />
              </div>
            </CustomUpload>
          </div>
          <Badge count={files.length}>
            <Button
              shape="circle"
              type="primary"
              icon={<SendOutlined />}
              aria-label="Send All"
              size="large"
              onClick={handleSendAll}
            />
          </Badge>
        </div>
      </div>
      {!files.length && (
        <CustomUpload
          onChange={handleFileSelect}
          accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.txt,.zip,.csv"
        >
          <Button
            type="text"
            shape="circle"
            icon={<PaperClipOutlined />}
            className="attach-button"
            aria-label="Attach file"
          />
        </CustomUpload>
      )}
    </div>
  );
};

export default FileUploadInput;
